/**
 * Pure fall-damage classifier (Phase 5 flow kernel).
 *
 * Bands a NET DROP distance (how far below the takeoff Y the cat landed) into
 * a severity + damage amount. No Babylon imports — safe for NullEngine/node
 * tests and for reuse by both the Basil harness sink and the Minna feedback
 * path.
 *
 * Thresholds are passed in (sourced from GameConfig.FALL_DAMAGE) so the
 * classifier stays config-agnostic and trivially testable.
 *
 * Why net-drop instead of impact speed?
 *   Impact speed (|vy| at landing) is contaminated by fast-fall input and the
 *   OverclockedLeap boost — a routine jump with fast-fall active hits ~12.7 m/s,
 *   which exceeds the old LIGHT_THRESHOLD (11) and wrongly chips Basil's harness
 *   on every jump. Net drop (lastGroundedY − landingY) is zero for any jump or
 *   OvLeap that returns to the same height, and is large only for genuine falls
 *   off ledges.
 *
 * Bands (lower bound inclusive):
 *   dropDistance <  lightThreshold              -> 'none'  / 0
 *   lightThreshold <= dropDistance < heavyThreshold -> 'light' / lightDamage
 *   dropDistance >= heavyThreshold              -> 'heavy'  / heavyDamage
 */
export type FallSeverity = "none" | "light" | "heavy";

export interface FallDamageInput {
  /** Net drop distance in world units: max(0, lastGroundedY - landingY). */
  dropDistance: number;
  lightThreshold: number;
  heavyThreshold: number;
  lightDamage: number;
  heavyDamage: number;
}

export interface FallDamageResult {
  severity: FallSeverity;
  damage: number;
}

export function computeFallDamage(i: FallDamageInput): FallDamageResult {
  const drop = Math.max(0, i.dropDistance);
  if (drop >= i.heavyThreshold) {
    return { severity: "heavy", damage: i.heavyDamage };
  }
  if (drop >= i.lightThreshold) {
    return { severity: "light", damage: i.lightDamage };
  }
  return { severity: "none", damage: 0 };
}
